import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import StickyHeadTable from 'shared/components/table/StickyHeadTable';
import { getTenantCreatePath, getTenantMovePath, getTenantPath } from 'shared/routes/tenant';
import { formatTenantName, getTenantSubscriptionQos } from 'shared/utils/formatters';
import { formatPhoneNumber } from 'shared/utils';
import { BUTTON_COLORS } from 'shared/constants';
import { useAuth, useRequest } from 'shared/hooks';
import { createTenantUnitReference } from 'shared/api/tenants';
import SplitButton from 'shared/components/SplitButton';
import { InfoModal } from 'shared/components/abstract/interactables';
import { GridWrapper } from 'shared/components/data-display/GridFormatter';

function tenantLink(id, name) {
  return <Link to={getTenantPath(id)}>{name}</Link>;
}

/**
 * @param {Object} props
 * @param {string} props.defaultSpeed
 * @param {boolean} props.editing
 * @param {Function} props.reloadUnit
 * @param {Array<Object>} [props.tenants=[]]
 * @param {number} props.buildingId
 * @param {number} props.unitId
 */
export default function TenantTable(props) {
  const {
    defaultSpeed,
    editing,
    reloadUnit,
    tenants = [],
    buildingId,
    unitId,
  } = props;

  const { user } = useAuth();
  const { request } = useRequest();
  const history = useHistory();
  const [selectedTenants, setSelectedTenants] = useState({});

  const archiveSelectedTenants = async () => {
    const result = await Promise.all(Object
      .values(selectedTenants)
      .map((t) => request(createTenantUnitReference(t.id, null, user.username))));
    if (result.some((r) => !!r)) {
      setSelectedTenants({});
      reloadUnit();
    }
  };

  const numberSelected = Object.values(selectedTenants).length;

  const columns = [
    {
      name: 'Tenant Name',
      formatter: (t) => tenantLink(t.id, formatTenantName(t)),
    },
    {
      name: 'Phone',
      formatter: (t) => formatPhoneNumber(t.phone),
    },
    {
      name: 'Email',
      jsonKey: 'email',
    },
    {
      name: 'Internet Speed',
      formatter: (t) => getTenantSubscriptionQos(t).description || defaultSpeed,
    },
  ];

  if (editing) {
    const onTenantSelect = (tenant) => {
      const selectedTenantsCpy = { ...selectedTenants };
      if (selectedTenants[tenant.id]) {
        delete selectedTenantsCpy[tenant.id];
      } else {
        selectedTenantsCpy[tenant.id] = tenant;
      }
      setSelectedTenants(selectedTenantsCpy);
    };
    const tenantSelect = (tenant) => (
      <input
        type="checkbox"
        value={tenant.id}
        checked={!!selectedTenants[tenant.id]}
        onChange={() => onTenantSelect(tenant)}
      />
    );

    columns.push({
      name: 'Select',
      onClick: () => {
        if (Object.values(selectedTenants)
          .some((d) => !!d)) {
          setSelectedTenants({});
        } else {
          const selectAll = {};
          tenants.forEach((tenant) => {
            selectAll[tenant.id] = tenant;
          });
          setSelectedTenants(selectAll);
        }
      },
      formatter: (tenant) => tenantSelect(tenant),
    });
  }

  return (
    <GridWrapper
      size={6}
      title="Tenants"
      titleIcon={(editing && numberSelected > 0)
        ? (
          <Grid>
            <InfoModal
              actionCallback={archiveSelectedTenants}
              actionName={numberSelected > 1 ? 'Archive Tenants' : 'Archive Tenant'}
              description={numberSelected > 1 ? `Archive ${numberSelected} tenants?` : 'Archive 1 tenant?'}
              title="Confirm"
              color={BUTTON_COLORS.warning}
            />
          </Grid>
        )
        : (
          <Grid>
            <SplitButton
              onClick={() => history.push(getTenantCreatePath(buildingId, unitId))}
              id="unit-tenant-actions"
              title="Add Tenant"
              menuItems={[{
                menuItemClick: () => history.push(getTenantMovePath(buildingId, unitId)),
                menuItemTitle: 'Move a tenant here',
              }]}
            />
          </Grid>
        )}
    >
      <StickyHeadTable
        noRowsText="No tenants found"
        className="table-height-xs"
        columns={columns}
        rowId="id"
        rows={tenants}
        striped
      />
    </GridWrapper>
  );
}
